import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  InsertNormativeBody,
  Normative,
  UpdateNormativeNewImageBody,
  UpdateNormativeRemoveImageBody,
} from "./types/normatives";

export const useNormativesApi = () => {
  const api = useApi();

  const getAdminNormative = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/normatives/${id}`;
      return useSWRMutation<Normative, CustomError, string>(url, (url) => api.get<Normative>({ url, signal }));
    },
    [api],
  );

  const useAdminNormatives = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/border/normatives", query);
      return useSWRImmutable<Paginated<Normative>, CustomError, string>(url, (url) =>
        api.get<Paginated<Normative>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminNormatives = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/border/normatives", query);
      return api.get<Paginated<Normative>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminNormative = useCallback(
    async (body: InsertNormativeBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/normatives`;
      const id = await api.post<{ id: string }, InsertNormativeBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminNormative = useCallback(
    async (normativeId: string, body: UpdateNormativeRemoveImageBody | UpdateNormativeNewImageBody): Promise<void> => {
      const url = `/admin/settings/border/normatives/${normativeId}`;
      (await api.put<UpdateNormativeRemoveImageBody | UpdateNormativeNewImageBody>(url, body),
        mutateCache(["/admin/settings/border/normatives"]));
    },
    [api],
  );

  const updateAdminNormativeVisibility = useCallback(
    async (normativeId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/normatives/${normativeId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache([`/admin/settings/border/normatives`]));
    },
    [api],
  );

  const updateAdminNormativeOrder = useCallback(
    async (normativeId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/normatives/${normativeId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order), mutateCache([`/admin/settings/border/normatives`]));
    },
    [api],
  );

  const deleteAdminNormative = useCallback(
    async (normativeId: string): Promise<void> => {
      const url = `/admin/settings/border/normatives/${normativeId}`;
      await api.del(url).then(() => {
        mutateCache([`/admin/settings/border/normatives`]);
      });
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (normativeId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/border/normatives/${normativeId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  return {
    getAdminNormative,
    useAdminNormatives,
    insertAdminNormative,
    updateAdminNormative,
    updateAdminNormativeVisibility,
    updateAdminNormativeOrder,
    deleteAdminNormative,
    generateUploadImageUrl,
    getAdminNormatives,
  };
};
